import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import type { IRedirectDomain } from '@feathr/blackbox';
import {
  ButtonValid,
  CardV2 as Card,
  DatePicker,
  Fieldset,
  ImagePicker,
  Input,
  Select,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import TagsField from '@feathr/extender/components/TagsField';
import { flattenError, flattenErrors } from '@feathr/hooks';

import ArchiveProjectToggle from './ArchiveProjectToggle';
import { useEventSettingsInfo } from './EventSettingsInfo.hooks';

function getOptionLabel(option: IRedirectDomain): string {
  return option.domain;
}

function EventSettingsInfo(): JSX.Element {
  const {
    event,
    t,
    localUrl,
    errors,
    startDate,
    endDate,
    isDirty,
    isAgency,
    hasInvites,
    redirectDomains,
    refPageRdr,
    mktPageRdr,
    advOptions,
    setRightActions,
    handleSave,
    handleChangeTags,
    handleChangeRefPageRdr,
    handleChangeMktgPageRdr,
    handleSelectAdvertiser,
    handleEndDateInput,
  } = useEventSettingsInfo();

  const hasErrors = flattenErrors(errors).length > 0;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setRightActions(
      <ButtonValid
        disabled={isDirty ? undefined : true}
        errors={flattenErrors(errors)}
        onClick={(): Promise<void> => handleSaveRef.current()}
        prefix={<FontAwesomeIcon icon={faSave} />}
      >
        {t('Save changes')}
      </ButtonValid>,
    );
  }, [isDirty, hasErrors]);

  // The event is loaded at the page level, so the selects only need to track their own fetch.
  const domainsLoading = redirectDomains.isPending;
  const domainPlaceholder = domainsLoading ? t('Loading...') : t('Select a domain...');

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t("Manage this project's details, schedule, page domains, and status.")}
      includeCurrentPageCrumb={true}
      loading={event.isPending}
      showHeader={true}
      title={t('Project Information')}
    >
      <Card name={'project-details-card'}>
        <Card.Header
          description={t("Your project's name, logo, and tags.")}
          title={t('Project details')}
        />
        <Card.Content addVerticalGap={true}>
          <ImagePicker attribute={'logo'} model={event} />
          <Input
            attribute={'name'}
            label={t('Project name')}
            model={event}
            type={'text'}
            validationError={flattenError(errors.name)}
          />
          <TagsField
            context={'event'}
            helpText={t(
              'Search for an existing tag, or type a new name to create one. Tags group related projects so you can filter for them in the projects page.',
            )}
            label={t('Tags')}
            onChange={handleChangeTags}
            value={event.get('tag_ids', [])}
          />
          <Input
            attribute={['email_preferences_alias']}
            helpText={t(
              'Feathr automatically creates an email subscription for each project. Customize the label recipients see when managing their subscription preferences. Defaults to the project name.',
            )}
            isClearable={true}
            label={t('Custom email subscription label')}
            model={event}
            placeholder={event.name}
            type={'text'}
          />
          {isAgency && (
            <Select
              helpText={
                <>
                  <p>
                    If this project will primarily house campaigns that advertise on behalf of
                    another organization, please choose that organization here. New campaigns will
                    use this advertiser by default, but it can be changed on a per-campaign basis.
                  </p>
                  <p>
                    Add organizations to this list on the{' '}
                    <Link to={localUrl('settings/account/advertisers')}>Advertisers page</Link> in
                    your account settings.
                  </p>
                </>
              }
              label={t('Default advertiser')}
              name={'default-advertiser'}
              onSelectSingle={handleSelectAdvertiser}
              options={advOptions}
              value={advOptions.find((s) => s.id === event.get('subadvertiser'))}
            />
          )}
        </Card.Content>
      </Card>

      <Card name={'schedule-card'}>
        <Card.Header
          description={t('Set when this project starts and ends.')}
          title={t('Schedule')}
        />
        <Card.Content>
          {/*
           * Clearing a date saves correctly, but the model stays dirty (Save stays enabled) until
           * reload: the PATCH response omits the now-null field, and rachis only cleans attributes
           * echoed back (collection.ts -> setAttributeClean).
           */}
          {/* onChangeRaw lands an out-of-range *typed* end date (which react-datepicker drops) on the
              model so the date_end constraint can flag it; picks still use minDate/maxDate. The range
              error is shown once, on the End field (it reads "End date must be on or after..."). */}
          <Fieldset>
            <DatePicker
              attribute={'date_start'}
              endDate={endDate}
              isClearable={true}
              label={t('Start date')}
              maxDate={endDate}
              model={event}
              selectsStart={true}
              startDate={startDate}
            />
            <DatePicker
              attribute={'date_end'}
              endDate={endDate}
              isClearable={true}
              label={t('End date')}
              minDate={startDate}
              model={event}
              onChangeRaw={handleEndDateInput}
              selectsEnd={true}
              startDate={startDate}
              validationError={flattenError(errors.date_end)}
            />
          </Fieldset>
        </Card.Content>
      </Card>

      <Card name={'domains-card'}>
        <Card.Header
          description={t("Choose the domains used for this project's pages.")}
          title={t('Domains')}
        />
        <Card.Content>
          <Fieldset direction={'column'}>
            {hasInvites && (
              <Select<IRedirectDomain>
                disabled={domainsLoading}
                getOptionLabel={getOptionLabel}
                isLoading={domainsLoading}
                label={t('Invite page domain')}
                name={'invite-page-domain'}
                onSelectSingle={handleChangeRefPageRdr}
                options={redirectDomains.models.map((d) => d.toJS())}
                placeholder={domainPlaceholder}
                value={refPageRdr && refPageRdr.toJS()}
              />
            )}
            <Select<IRedirectDomain>
              disabled={domainsLoading}
              getOptionLabel={getOptionLabel}
              isLoading={domainsLoading}
              label={t('Landing page domain')}
              name={'landing-page-domain'}
              onSelectSingle={handleChangeMktgPageRdr}
              options={redirectDomains.models.map((d) => d.toJS())}
              placeholder={domainPlaceholder}
              value={mktPageRdr && mktPageRdr.toJS()}
            />
          </Fieldset>
        </Card.Content>
      </Card>
      <Card name={'archive-card'}>
        <ArchiveProjectToggle event={event} />
      </Card>
    </Page>
  );
}

export default observer(EventSettingsInfo);
